import React from 'react';
import {
  isMobile
} from "react-device-detect";
import Button from "react-bootstrap/Button";
import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
// import {map, size} from 'lodash';
import size from 'lodash/size';
import map from 'lodash/map';
import './styles.scss';
import Item from "./Item";
import PromoModal from '../PromoModal';
import ContactUsModal from '../ContactUsModal'
import LoginModal from '../LoginModal';
import BookingNumberModal from '../BookingNumberModal';
import ChatModal from '../ChatModal';
import ConfirmationModal from '../ConfirmationModal';
import { useTranslation } from 'react-i18next';
import { useEffect, useState, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import InfiniteScroll from "react-infinite-scroll-component";

import Tooltips from '../Tooltip';
import { Alert, Tabs, Tab } from 'react-bootstrap';
import { FaStar } from 'react-icons/fa';
import { getSearchKey } from '../../utils/searchKey';
import { useDispatch, useSelector } from 'react-redux';
import { addRecentSearch, saveRecentSearchRequest } from '../../redux/recentSearches/recentSearchesSlice';
import { clearBookingData } from '../../redux/booking/actions';
import { useRecentSearches } from '../../hooks/useRecentSearches';
import { useUserProfile } from '../../hooks/useUserProfile';
import { buildSearchData, getFiltersToStore, getStoredSavedOffers } from '../../utils/searchDataUtils';
import { isSameOffer } from '../../utils/offerIdMatch';
import { isWinterPeriod } from '../../utils/filterUtils';
import ChatWidget from '../ChatWidget';
import BookingModal from '../BookingModal';
import { parseDate } from '../../utils/dateUtils';
import { trackEvent } from '../../utils/analytics';

const RECENT_SEARCHES_KEY = 'recentSearchesV2';

const SAVED_OFFERS_KEY = 'savedOffers';

const SearchResults = ({ dir, data = [], onClear, cid, isAirport, locationSlug, allResults, params, activeFilters, locations, highlightOfferId, bookOfferId, onBookClose }) => {
  const [showPromo, setShowPromo] = useState(false);
  const [showIntro, setShowIntro] = useState(true);
  const [showContactUs, setShowContactUs] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [showBookingNumber, setShowBookingNumber] = useState(false);
  const [showChatModal, setShowChatModal] = useState(false);
  const [clickedItem, setClickedItem] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationData, setConfirmationData] = useState(null);

  // Calculate once if we're in winter period for efficiency
  const isWinterSearch = isWinterPeriod(params?.startDate, params?.endDate);

  // Get the current search key
  const searchKey = getSearchKey(params);

  const dispatch = useDispatch();
  // Get recent searches from Redux
  const recentSearches = useSelector(state => state.recentSearches.recent);
  // Get user state to check authentication
  const user = useSelector(state => state.user.user);
  // Get booking state
  const booking = useSelector(state => state.booking);
  // Starred/saved offers state (per search)
  const [savedOffers, setSavedOffers] = useState([]);
  const lastRestoredKey = useRef();

  const history = useHistory();

  // Use the consolidated hook for saving
  const { saveSearchWithImmediateUpdate } = useRecentSearches();

  // User profile hook for activity tracking
  const { trackSearchActivity } = useUserProfile({
    autoFetch: false // We don't need full profile here, just activity tracking
  });

  // Ref to track the last searchKey that was saved to prevent duplicate saves
  const lastSavedSearchKeyRef = useRef(null);
  const lastSavedFiltersRef = useRef(null);

  // Restore savedOffers from Redux only when searchKey changes
  useEffect(() => {
    if (!params) return;
    const searchKey = getSearchKey(params);
    if (lastRestoredKey.current === searchKey) return;
    const found = recentSearches.find(r => r.searchKey === searchKey);
    if (found && Array.isArray(found.savedOffers) && JSON.stringify(found.savedOffers) !== JSON.stringify(savedOffers)) {
      setSavedOffers(found.savedOffers);
    } else if (!found && savedOffers.length > 0) {
      setSavedOffers([]);
    }
    lastRestoredKey.current = searchKey;
  }, [params, recentSearches]);

  // Save search to history when params change (new search)
  useEffect(() => {
    if (!params) return;
    // Key ignores age/residency/cur, so include them here to re-save when they change
    const currentSearchKey = [getSearchKey(params), params.age, params.residency, params.cur].join('|');
    const filtersToStore = getFiltersToStore(activeFilters);
    const currentFiltersKey = JSON.stringify(filtersToStore);

    // Only save if this search or its filters changed since the last save
    if (lastSavedSearchKeyRef.current !== currentSearchKey || lastSavedFiltersRef.current !== currentFiltersKey) {
      const isNewSearch = lastSavedSearchKeyRef.current !== currentSearchKey;
      // Use this search's stored stars. Local state may be empty (mount) or belong to the previous search.
      const storedOffers = getStoredSavedOffers(recentSearches, params);
      setSavedOffers(storedOffers);
      const searchData = buildSearchData(params, storedOffers, filtersToStore);
      saveSearchWithImmediateUpdate(searchData);

      // Track search activity for analytics only when the trip itself changes, not on filter changes
      if (isNewSearch) {
        try {
          trackSearchActivity(params);
        } catch (error) {
          // Activity tracking is optional, don't break the flow
          console.warn('Failed to track search activity:', error);
        }
      }

      lastSavedSearchKeyRef.current = currentSearchKey;
      lastSavedFiltersRef.current = currentFiltersKey;
    }
  }, [params, activeFilters, saveSearchWithImmediateUpdate, trackSearchActivity]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle booking success
  useEffect(() => {
    if (booking.data) {
      setConfirmationData(booking.data);
      setShowConfirmation(true);
      dispatch(clearBookingData());
    }
  }, [booking.data, dispatch]);

  // Toggle star/unstar for an offer (per search). Unstarring removes both the current and legacy ID.
  const toggleStarOffer = (item) => {
    const isStarred = savedOffers.some(id => isSameOffer(item, id));
    const eventName = isStarred ? 'Unstar Offer' : 'Star Offer';
    trackEvent(eventName, {
      'event_category': 'Saved Offers',
      'event_label': item.id,
      'search_key': searchKey,
      // Optionally add more context (e.g., car model, supplier)
    });
    const newSavedOffers = isStarred
      ? savedOffers.filter((id) => !isSameOffer(item, id))
      : [...savedOffers, item.id];

    setSavedOffers(newSavedOffers);
    saveSearchWithImmediateUpdate(buildSearchData(params, newSavedOffers, getFiltersToStore(activeFilters)));
  };

  const setModalVisible = (visible => {
    // console.log(`setShow: ${visible}`)
    setShowPromo(visible)
  })

  const handlePrimaryClick = (item) => {
    setShowPromo(false);
    if (user) {
      setShowBookingNumber(true);
    } else {
      setShowLogin(true);
    }
  }

  const isPickupAtLeastTwoWeeksAway = () => {
    if (!params?.startDate) return false;
    const today = new Date();
    const pickupDate = parseDate(params.startDate);
    const diffTime = pickupDate - today;
    const diffDays = diffTime / (1000 * 60 * 60 * 24);
    return diffDays >= 14;
  };

  const itemClicked = (item) => {
    setClickedItem(item);
    if (isPickupAtLeastTwoWeeksAway()) {
      setShowPromo(true);
    } else {
      setShowChatModal(true);
    }
  }

  const [scroll, setScroll] = useState(10);

  const { t, i18n } = useTranslation();
  //  const locale = LOCALES[i18n.language.substring(0,2)];
  // const locale = i18n.language;

  const trackOffersCount = (count) => {
    trackEvent('Offers found', {
      'event_category': count === 0 ? 'Requirements' : 'Rating',
      // 'event_label': count,
      'value': count,
    });
    // console.log(count);
  }

  useEffect(() => {
    trackOffersCount(data.length);
  }, []);

  const intro = (
    <Alert key="intro" show={showIntro} variant="info">
      <Alert.Heading>{t("introHeader")}</Alert.Heading>
      <p>
        {t("intro")}
      </p>
      <hr />
      <div className="d-flex justify-content-end">
        <Button onClick={() => setShowIntro(false)} variant="outline-info">
          {t("gotIt")}
        </Button>&nbsp;
        <Button onClick={() => setShowContactUs(true)} variant="outline-dark">
          <svg fill="green" height="24" width="24" version="1.1" id="Layer_1" viewBox="0 0 308 308">
            <g id="XMLID_468_">
              <path id="XMLID_469_" d="M227.904,176.981c-0.6-0.288-23.054-11.345-27.044-12.781c-1.629-0.585-3.374-1.156-5.23-1.156
                c-3.032,0-5.579,1.511-7.563,4.479c-2.243,3.334-9.033,11.271-11.131,13.642c-0.274,0.313-0.648,0.687-0.872,0.687
                c-0.201,0-3.676-1.431-4.728-1.888c-24.087-10.463-42.37-35.624-44.877-39.867c-0.358-0.61-0.373-0.887-0.376-0.887
                c0.088-0.323,0.898-1.135,1.316-1.554c1.223-1.21,2.548-2.805,3.83-4.348c0.607-0.731,1.215-1.463,1.812-2.153
                c1.86-2.164,2.688-3.844,3.648-5.79l0.503-1.011c2.344-4.657,0.342-8.587-0.305-9.856c-0.531-1.062-10.012-23.944-11.02-26.348
                c-2.424-5.801-5.627-8.502-10.078-8.502c-0.413,0,0,0-1.732,0.073c-2.109,0.089-13.594,1.601-18.672,4.802
                c-5.385,3.395-14.495,14.217-14.495,33.249c0,17.129,10.87,33.302,15.537,39.453c0.116,0.155,0.329,0.47,0.638,0.922
                c17.873,26.102,40.154,45.446,62.741,54.469c21.745,8.686,32.042,9.69,37.896,9.69c0.001,0,0.001,0,0.001,0
                c2.46,0,4.429-0.193,6.166-0.364l1.102-0.105c7.512-0.666,24.02-9.22,27.775-19.655c2.958-8.219,3.738-17.199,1.77-20.458
                C233.168,179.508,230.845,178.393,227.904,176.981z"/>
              <path id="XMLID_470_" d="M156.734,0C73.318,0,5.454,67.354,5.454,150.143c0,26.777,7.166,52.988,20.741,75.928L0.212,302.716
                c-0.484,1.429-0.124,3.009,0.933,4.085C1.908,307.58,2.943,308,4,308c0.405,0,0.813-0.061,1.211-0.188l79.92-25.396
                c21.87,11.685,46.588,17.853,71.604,17.853C240.143,300.27,308,232.923,308,150.143C308,67.354,240.143,0,156.734,0z
                M156.734,268.994c-23.539,0-46.338-6.797-65.936-19.657c-0.659-0.433-1.424-0.655-2.194-0.655c-0.407,0-0.815,0.062-1.212,0.188
                l-40.035,12.726l12.924-38.129c0.418-1.234,0.209-2.595-0.561-3.647c-14.924-20.392-22.813-44.485-22.813-69.677
                c0-65.543,53.754-118.867,119.826-118.867c66.064,0,119.812,53.324,119.812,118.867
                C276.546,215.678,222.799,268.994,156.734,268.994z"/>
            </g>
          </svg>
          &nbsp;{t("waitAminute")}
        </Button>
      </div>
    </Alert>
  )

  // Filter offers for the starred tab (ignore filters, show all starred offers)
  const starredOffers = allResults.filter(item => savedOffers.some(id => isSameOffer(item, id)));
  const missingStarred = savedOffers.filter(id => !allResults.some(item => isSameOffer(item, id)));

  // Filters do not apply to the highlighted offer, so it is looked up in all results
  const highlightedOffers = highlightOfferId ? allResults.filter(item => isSameOffer(item, highlightOfferId)) : [];

  // Opened from a direct booking link. Only direct-booking offers open the modal, and it is never submitted automatically.
  const bookedOffer = bookOfferId ? allResults.find(item => item.directBooking && isSameOffer(item, bookOfferId)) : undefined;

  return (
    <div className={"results-section " + (dir == "rtl" ? "pr-md-3" : "pl-md-3")}> 
      {bookedOffer && (
        <BookingModal show onHide={onBookClose} offer={bookedOffer} isAirport={isAirport} dir={dir} cid={cid} />
      )}
      {highlightOfferId && (
        highlightedOffers.length > 0 ? (
          <div className="highlighted-offers mb-3">
            <Alert variant="info" className="mb-2">
              {t('highlightedOffer') || 'The offer you were looking at is shown below.'}
            </Alert>
            {map(highlightedOffers, (item) => (
              <Item
                key={`highlight-${item.id}`}
                item={item}
                dir={dir}
                onItemClicked={itemClicked}
                cid={cid}
                isAirport={isAirport}
                locationSlug={locationSlug}
                savedOffers={savedOffers}
                toggleStarOffer={toggleStarOffer}
                searchKey={searchKey}
                params={params}
                isWinterSearch={isWinterSearch}
                variant={isMobile ? 'mobile' : 'desktop'}
              />
            ))}
          </div>
        ) : (
          <Alert variant="warning" className="mb-3">
            {t('highlightedOfferNotFound') || 'The offer you were looking for is no longer in these results. All current offers are listed below.'}
          </Alert>
        )
      )}
      {bookOfferId && !bookedOffer && (
        <Alert variant="warning" className="mb-3">
          {t('highlightedOfferNotFound') || 'The offer you were looking for is no longer in these results. All current offers are listed below.'}
        </Alert>
      )}
      <Tabs
        id="offers-tabs"
        activeKey={activeTab}
        onSelect={k => setActiveTab(k)}
        className="mb-3"
      >
        <Tab eventKey="all" title={t('allOffers') || 'All Offers'} />
        <Tab eventKey="starred" title={<span><FaStar color="#f5c518" style={{marginBottom: 2}}/> {t('savedOffers') || 'Saved Offers'} <span className="badge badge-secondary">{savedOffers.length}</span></span>} />
      </Tabs>
      {activeTab === 'starred' && (
        <>
          <Alert variant="info" className="mb-2">
            {t('filtersDoNotAffectStarred') || 'Filters do not affect the Saved Offers tab.'}
          </Alert>
          {missingStarred.length > 0 && (
            <Alert variant="warning" className="mb-2">
              {t('missingStarredOffers') || 'Some starred offers are no longer available.'}
            </Alert>
          )}
        </>
      )}
      {/* Filters always visible */}
      {/* ...existing code for filters... */}
      {activeTab === 'starred' ? (
        map(starredOffers, (item, index) => {
          const elements = [];

          elements.push(
            <Item
              key={index}
              item={item}
              dir={dir}
              onItemClicked={itemClicked}
              cid={cid}
              isAirport={isAirport}
              locationSlug={locationSlug}
              savedOffers={savedOffers}
              toggleStarOffer={toggleStarOffer}
              searchKey={searchKey}
              params={params}
              isWinterSearch={isWinterSearch}
              variant={isMobile ? 'mobile' : 'desktop'}
            />
          );

          return elements;
        })
      ) : (
        size(data) > 0 ? (
          <InfiniteScroll
            dataLength={Math.min(scroll, data.length)}
            next={() => setScroll(scroll + 10)}
            hasMore={scroll < data.length}
            loader={<div className="text-center py-2">{t('loadingResults')}</div>}
          >
            {map(data.slice(0, scroll), (item, index) => {
              const elements = [];
              // if (index === 6) elements.push(<ChatWidget key="chat-widget" dir={dir} params={params} activeFilters={activeFilters} locations={locations} onShowModal={() => setShowChatModal(true)} isModalOpen={showChatModal} />);
              if (index === 2) elements.push(intro);
              elements.push(
                <Item
                  key={index}
                  item={item}
                  dir={dir}
                  onItemClicked={itemClicked}
                  cid={cid}
                  isAirport={isAirport}
                  locationSlug={locationSlug}
                  savedOffers={savedOffers}
                  toggleStarOffer={toggleStarOffer}
                  searchKey={searchKey}
                  params={params}
                  isWinterSearch={isWinterSearch}
                  variant={isMobile ? 'mobile' : 'desktop'}
                />
              );
              return elements;
            })}
          </InfiniteScroll>
        ) : (
          <Alert variant="warning" className="mt-3">{t('tooManyFilters')}</Alert>
        )
      )}
      <PromoModal dir={dir} shouldShow={showPromo} setModalVisible={setModalVisible} cid={cid} user={user} onPrimaryClick={handlePrimaryClick} clickedItem={clickedItem}></PromoModal>
      <ContactUsModal dir={dir} shouldShow={showContactUs} setModalVisible={setShowContactUs} ></ContactUsModal>
      <LoginModal shouldShow={showLogin} setModalVisible={setShowLogin} afterLogin={() => { setShowLogin(false); setShowBookingNumber(true); }} cid={cid} />
      <BookingNumberModal dir={dir} shouldShow={showBookingNumber} setModalVisible={setShowBookingNumber} cid={cid} item={clickedItem} searchKey={searchKey} />
      <ChatModal dir={dir} shouldShow={false} setModalVisible={setShowChatModal} params={params} activeFilters={activeFilters} locations={locations} />
      <ConfirmationModal
        show={showConfirmation}
        onHide={() => setShowConfirmation(false)}
        onGoToProfile={() => {
          setShowConfirmation(false);
          history.push(`/${i18n.language}/profile`);
        }}
        data={confirmationData}
        dir={dir}
      />
      <Tooltips />
    </div>
  );
};

export default SearchResults;
